// OIDC Authorization Code + PKCE flow for Keycloak

import { KC_BASE_URL } from './runtimeEnv.js';

const KC_BASE   = `${KC_BASE_URL}/realms/parsina`;

// The OIDC client id, baked in at BUILD time by the Dockerfile ARG
// (`PARSINA_KC_CLIENT_ID=parsina-student`). Parcel inlines `process.env.*`, so
// this is NOT reachable through window.__ENV__ and is deliberately not routed
// through runtimeEnv.js like BFF_URL is.
//
// The fallback is `parsina-student`, NOT the `parsina-web` the doctor, clinic
// and dentist clients default to. That is the one difference in this file and
// it is load-bearing: `parsina-web` is shared by the doctor and clinic panels,
// so a token minted for it opens doctor.parsinabot.ir. The audience separation
// IS this app's audience separation (core.md §1.4) — a build that silently fell
// back to the shared client would hand every student a doctor's token.
const CLIENT_ID = process.env.PARSINA_KC_CLIENT_ID || 'parsina-student';

// Always derived at call time so redirect_uri is consistent across
// the auth request and token exchange even if the page reloads.
function getRedirectUri() {
  return `${window.location.origin}/`;
}

// ── PKCE helpers ─────────────────────────────────────────────────────────────

function randomBytes(len) {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return arr;
}

function base64url(buf) {
  return btoa(String.fromCharCode(...buf))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function sha256(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return new Uint8Array(buf);
}

async function generateChallenge(verifier) {
  return base64url(await sha256(verifier));
}

// ── Token storage ─────────────────────────────────────────────────────────────

export function getToken()   { return sessionStorage.getItem('kc_token'); }
export function getRefresh() { return sessionStorage.getItem('kc_refresh'); }
export function getIdToken() { return sessionStorage.getItem('kc_id_token'); }

function saveTokens(token, refresh, idToken) {
  sessionStorage.setItem('kc_token', token);
  if (refresh) sessionStorage.setItem('kc_refresh', refresh);
  if (idToken) sessionStorage.setItem('kc_id_token', idToken);
}

export function clearTokens() {
  ['kc_token', 'kc_refresh', 'kc_id_token', 'kc_verifier', 'kc_state'].forEach(k =>
    sessionStorage.removeItem(k)
  );
}

// ── Redirect-loop guard ───────────────────────────────────────────────────────
// Records each redirect timestamp. If 3+ happen within 30 s, stops redirecting
// and lets the app render an error screen instead.

const LOOP_KEY   = 'kc_redirect_ts';
const LOOP_LIMIT = 3;
const LOOP_WIN   = 30_000; // ms

function redirectAllowed() {
  const raw    = sessionStorage.getItem(LOOP_KEY);
  const hist   = raw ? JSON.parse(raw) : [];
  const now    = Date.now();
  const recent = hist.filter(ts => now - ts < LOOP_WIN);
  if (recent.length >= LOOP_LIMIT) return false;
  sessionStorage.setItem(LOOP_KEY, JSON.stringify([...recent, now]));
  return true;
}

export function clearRedirectGuard() {
  sessionStorage.removeItem(LOOP_KEY);
}

// ── Auth flow ─────────────────────────────────────────────────────────────────

export async function redirectToLogin() {
  if (!redirectAllowed()) {
    // Too many consecutive redirects — surface the error to the user instead.
    clearTokens();
    window.history.replaceState({}, '', '/?auth_error=loop');
    window.location.reload();
    return;
  }

  const verifier  = base64url(randomBytes(32));
  const challenge = await generateChallenge(verifier);
  const state     = base64url(randomBytes(16));

  sessionStorage.setItem('kc_verifier', verifier);
  sessionStorage.setItem('kc_state',    state);

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             CLIENT_ID,
    redirect_uri:          getRedirectUri(),
    scope:                 'openid profile email',
    state,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `${KC_BASE}/protocol/openid-connect/auth?${params}`;
}

// There is deliberately NO silent `reauthorize()` (prompt=none) redirect here.
// Onboarding grants the `student` client role server-side (core.md §4.3), and
// the token picks it up on the next REFRESH — `refreshAfterOnboarding()` below
// is one silent refresh-token call, not an auth redirect that would unmount the
// app the student just finished a form in. The role is informational today (no
// service authorizes on it), so a failed refresh is logged and ignored.

/**
 * Refresh the access token once after `completeStudentOnboarding`, so
 * `resource_access` carries the role the BFF just granted. Never throws; the
 * profile — not the role — is what makes someone a student here.
 */
export async function refreshAfterOnboarding() {
  try {
    return Boolean(await refreshAccessToken());
  } catch (err) {
    console.warn('[auth] post-onboarding refresh failed:', err?.message);
    return false;
  }
}

// ── The one callback error that is NOT "try signing in again" ────────────────
//
// `invalid_redirect_uri` means THIS ORIGIN is not registered on the
// `parsina-student` client in Keycloak. Retrying cannot fix it, and treating it
// like `access_denied` (clear tokens → sign-in screen → the student presses the
// button → the same error) is a loop with a human in it. core.md §7.1 asks for
// a plain explanation instead (`login.badRedirect`), which the sign-in screen
// renders when `takeCallbackError()` says so.
//
// Keycloak spells it as `error=invalid_redirect_uri` on the redirect, and as
// «Invalid parameter: redirect_uri» in the description when it does redirect at
// all — both are matched, because which one arrives depends on the realm's
// version and on whether it could match a redirect at all.

export const CALLBACK_ERROR_BAD_REDIRECT = 'bad_redirect';

let lastCallbackError = null;

/** Why the last `handleCallback()` returned false, when the reason matters. One-shot. */
export function takeCallbackError() {
  const reason = lastCallbackError;
  lastCallbackError = null;
  return reason;
}

export const isBadRedirectError = (error, description) =>
  error === 'invalid_redirect_uri'
  || /invalid_redirect_uri|redirect[_ ]uri/i.test(String(description || ''));

export async function handleCallback() {
  const params   = new URLSearchParams(window.location.search);
  const error    = params.get('error');
  const code     = params.get('code');
  const state    = params.get('state');
  const verifier = sessionStorage.getItem('kc_verifier');
  const saved    = sessionStorage.getItem('kc_state');

  lastCallbackError = null;
  console.log('[handleCallback] error:', error, '| hasCode:', !!code, '| stateMatch:', state === saved, '| hasVerifier:', !!verifier);

  // Keycloak returned an explicit error (access_denied, login_required, etc.)
  if (error) {
    const description = params.get('error_description');
    console.warn('[handleCallback] KC returned error:', error, description);
    if (isBadRedirectError(error, description)) lastCallbackError = CALLBACK_ERROR_BAD_REDIRECT;
    clearTokens();
    window.history.replaceState({}, '', '/');
    return false;
  }

  // Missing or mismatched PKCE / state — don't retry immediately
  if (!code || !verifier || state !== saved) {
    console.warn('[handleCallback] missing code/verifier or state mismatch — aborting');
    window.history.replaceState({}, '', '/');
    return false;
  }

  const res = await fetch(`${KC_BASE}/protocol/openid-connect/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type:    'authorization_code',
      client_id:     CLIENT_ID,
      redirect_uri:  getRedirectUri(),
      code,
      code_verifier: verifier,
    }),
  });

  window.history.replaceState({}, '', '/');

  if (!res.ok) {
    console.error('[handleCallback] token exchange failed:', res.status, await res.text());
    return false;
  }

  const data = await res.json();
  if (!data.access_token) {
    // The FIELDS, never the payload: a token-endpoint response is the one body
    // in this app that can carry a bearer credential, and the browser console
    // is readable by any script on the page and by every support screenshot.
    console.error(
      '[handleCallback] no access_token in response. keys:', Object.keys(data || {}).join(','),
      '| error:', data?.error, data?.error_description,
    );
    return false;
  }

  // Log roles from new token
  try {
    const claims = JSON.parse(atob(data.access_token.split('.')[1].replace(/-/g,'+').replace(/_/g,'/')));
    // `student` may or may not be here: v2 gates on the onboarded PROFILE, not
    // the role (core.md §4.2). There is no clinic_id on a student token.
    const roles = claims?.resource_access?.['parsina-bff']?.roles || [];
    console.log('[handleCallback] new token roles:', roles, '| sub:', claims?.sub);
  } catch { /* non-fatal */ }

  saveTokens(data.access_token, data.refresh_token, data.id_token);
  clearRedirectGuard();
  console.log('[handleCallback] tokens saved successfully');
  return true;
}

// Concurrent refresh, deduplicated.
//
// Reads fire in parallel on every screen — the case workspace issues the case,
// its history and its chat threads together, and the layout adds
// `studentProfile` + `myWalletBalance` on top. When the access token has expired
// they ALL reach getValidToken() at the same moment and each calls this with the
// SAME refresh token.
//
// That is a race with a session-ending outcome: Keycloak rotates refresh
// tokens, and with «Revoke Refresh Token» enabled the second presentation of
// one is `invalid_grant` — so the loser's `clearTokens()` below wipes the
// tokens the winner had just saved, and the next request finds no token and
// bounces to login. Even without revocation the two `saveTokens()` calls race
// and the older pair can land last.
//
// One in-flight promise shared by every caller removes the race in every realm
// configuration and costs one round trip instead of N. The exported signature
// is unchanged: still a Promise<boolean>.
let refreshInFlight = null;

export function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;
  // `.finally` passes the resolution value through, so callers still receive
  // the boolean; the slot is cleared either way so a later expiry can retry.
  refreshInFlight = performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function performRefresh() {
  const refresh = getRefresh();
  if (!refresh) return false;

  const res = await fetch(`${KC_BASE}/protocol/openid-connect/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type:    'refresh_token',
      client_id:     CLIENT_ID,
      refresh_token: refresh,
    }),
  });

  if (!res.ok) { clearTokens(); return false; }
  const data = await res.json();
  if (!data.access_token) { clearTokens(); return false; }
  saveTokens(data.access_token, data.refresh_token, data.id_token);
  return true;
}

export function logout() {
  const idToken = getIdToken();
  clearTokens();
  clearRedirectGuard();
  const params = new URLSearchParams({
    client_id:                CLIENT_ID,
    post_logout_redirect_uri: getRedirectUri(),
  });
  if (idToken) params.set('id_token_hint', idToken);
  window.location.href = `${KC_BASE}/protocol/openid-connect/logout?${params}`;
}

export function parseJwt(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

export function isTokenExpired(token) {
  const p = parseJwt(token);
  if (!p) return true;
  return Date.now() >= p.exp * 1000 - 30_000; // 30 s buffer
}
